import React from "react";
import { User } from "~/common/Store/models/User";
import { PricingProduct } from "~/ProductDetails/Pricing/PricingProduct";
import shouldDisplayQuantityDiscountRibbon from "./shouldDisplayQuantityDiscountRibbon";
import shouldDisplayMapStyle from "~/common/Product/shouldDisplayMapStyle";
import Ribbon from "~/common/Ribbon/primitive";
import { Icon, Tooltip } from "@clarkinc/zest-design-system/ui";
import RibbonWithIcon from "~/common/Ribbon/variants/ribbonWithIcon";

import("./RibbonsStyling.scss");

interface Props {
  product: PricingProduct;
  user: User;
  showGoogleView?: boolean;
  hideQuantityDiscount?: boolean;
}

const questionMarkIcon = (
  <Tooltip
    content="Contiguous U.S. Only"
    theme="wss"
    aria-label="more info about free shipping"
  >
    <button className="bg-transparent border-none p-0 h-4 w-4">
      <Icon
        className="fill-white"
        size="xsmall"
        name="ri-question-fill"
        data-testid="free-shipping-tooltip-trigger"
      />
    </button>
  </Tooltip>
);

const arrowIcon = (
  <Icon
    theme="wss"
    className="fill-white"
    size="xsmall"
    name="ri-arrow-right-s-line"
  />
);

const Ribbons = ({
  product,
  user,
  showGoogleView = false,
  hideQuantityDiscount = false,
}: Props) => {
  const {
    isNewProduct,
    mixAndMatchUrl,
    purchasingGroupUrl,
    isWebstaurantPlusEligible,
    hasFreeShipping,
  } = product;

  const displayQuantityDiscounts = hideQuantityDiscount
    ? false
    : shouldDisplayQuantityDiscountRibbon({ ...product }, user);

  const displayMapStyle = shouldDisplayMapStyle(product, user);

  const ribbonTextClasses =
    "text-sm-1/2 text-white leading-none font-semibold [text-shadow:0_1px_2px_#0003]";

  const roundedClasses = "rounded-tr-[4px] rounded-bl-[4px]";

  return (
    <div className="flex flex-row flex-wrap gap-1-1/2 mb-2-1/2 -mt-2-1/2 -ml-6 xs:-ml-5 lt:-ml-7">
      {/* New Product */}
      {isNewProduct && (
        <Ribbon className={`bg-green-400 ${roundedClasses}`}>
          <span className={ribbonTextClasses}>New</span>
        </Ribbon>
      )}
      {/* Mix and Match Discounts */}
      {!displayMapStyle && mixAndMatchUrl && (
        <RibbonWithIcon
          as="a"
          href={mixAndMatchUrl}
          icon={arrowIcon}
          className={`bg-[#1ab1a0] hover:bg-[#0d8b7d] ${roundedClasses}`}
        >
          <span className={ribbonTextClasses}>Mix & Match Discounts</span>
        </RibbonWithIcon>
      )}
      {/* Mix and Match Purchasing Group */}
      {purchasingGroupUrl && (
        <RibbonWithIcon
          as="a"
          href={purchasingGroupUrl}
          icon={arrowIcon}
          className={`bg-[#1ab1a0] hover:bg-[#0d8b7d] ${roundedClasses}`}
        >
          <span className={ribbonTextClasses}>Mix & Match</span>
        </RibbonWithIcon>
      )}
      {/* Quantity Discounts base */}
      {displayQuantityDiscounts && !showGoogleView && (
        <Ribbon className={`bg-orange-600 ${roundedClasses}`}>
          <span className={ribbonTextClasses}>Quantity Discounts</span>
        </Ribbon>
      )}
      {/* Quantity Discounts Google View */}
      {displayQuantityDiscounts && showGoogleView && (
        <RibbonWithIcon
          as="a"
          href={product.link}
          icon={arrowIcon}
          className={`bg-orange-600 ${roundedClasses}`}
        >
          <span className={ribbonTextClasses}>View Quantity Discounts</span>
        </RibbonWithIcon>
      )}
      {/* Free Shipping */}
      {hasFreeShipping && !(user.hasWebPlus && isWebstaurantPlusEligible) && (
        <RibbonWithIcon
          icon={questionMarkIcon}
          className={`bg-blue-800 ${roundedClasses}`}
          data-testid="free-shipping-ribbon"
        >
          <span className={`${ribbonTextClasses} mr-1`}>Free Shipping</span>
        </RibbonWithIcon>
      )}
    </div>
  );
};

export default Ribbons;
